import {
  type Column,
  type ColumnDef,
  type ColumnPinningState,
  type ColumnSizingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  useReactTable
} from '@tanstack/react-table';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from 'lucide-react';
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';

import { Empty, EmptyContent } from '@/components/ui/empty';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface DataTableColumnMeta {
  cellClassName?: string;
  headerClassName?: string;
}

interface DataTableProps<TData, TValue> {
  className?: string;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  emptyAction?: ReactNode;
  emptyMessage?: string;
  enableColumnResizing?: boolean;
  enableRowSelection?: boolean;
  getRowId?: (row: TData) => string;
  initialColumnPinning?: ColumnPinningState;
  manualSorting?: boolean;
  onRowClick?: (row: TData) => void;
  onSortingChange?: OnChangeFn<SortingState>;
  persistColumnSizingKey?: string;
  selectedRowId?: null | string;
  sorting?: SortingState;
}

const COLUMN_SIZING_STORAGE_PREFIX = 'data-table-column-sizing:';
const MAX_COLUMN_SIZE = 2000;
const MIN_COLUMN_SIZE = 24;

export function DataTable<TData, TValue>({
  className,
  columns,
  data,
  emptyAction,
  emptyMessage = 'No results.',
  enableColumnResizing = false,
  enableRowSelection = false,
  getRowId,
  initialColumnPinning,
  manualSorting = false,
  onRowClick,
  onSortingChange,
  persistColumnSizingKey,
  selectedRowId,
  sorting
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const resolvedColumnSizingStorageKey = persistColumnSizingKey
    ? `${COLUMN_SIZING_STORAGE_PREFIX}${persistColumnSizingKey}`
    : null;
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
    resolvedColumnSizingStorageKey
      ? readStoredColumnSizing(resolvedColumnSizingStorageKey)
      : {}
  );

  const resolvedSorting = sorting ?? internalSorting;

  const handleSortingChange: OnChangeFn<SortingState> = updater => {
    if (sorting === undefined) {
      setInternalSorting(current =>
        typeof updater === 'function' ? updater(current) : updater
      );
    }

    onSortingChange?.(updater);
  };

  const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = updater => {
    setColumnSizing(current => {
      const nextValue =
        typeof updater === 'function' ? updater(current) : updater;
      return sanitizeColumnSizing(nextValue);
    });
  };

  useEffect(() => {
    if (!resolvedColumnSizingStorageKey) {
      return;
    }

    setColumnSizing(readStoredColumnSizing(resolvedColumnSizingStorageKey));
  }, [resolvedColumnSizingStorageKey]);

  useEffect(() => {
    if (!resolvedColumnSizingStorageKey) {
      return;
    }

    writeStoredColumnSizing(resolvedColumnSizingStorageKey, columnSizing);
  }, [columnSizing, resolvedColumnSizingStorageKey]);

  const getCommonPinningStyles = (
    column: Column<TData, TValue>,
    isHeader = false
  ): CSSProperties => {
    const isPinned = column.getIsPinned();
    const isFirstRightPinnedColumn =
      isPinned === 'right' && column.getIsFirstColumn('right');
    const isLastLeftPinnedColumn =
      isPinned === 'left' && column.getIsLastColumn('left');

    if (!isPinned) {
      return {};
    }

    const edgeShadow = isLastLeftPinnedColumn
      ? '-4px 0 4px -4px var(--border) inset'
      : isFirstRightPinnedColumn
        ? '4px 0 4px -4px var(--border) inset'
        : undefined;

    return {
      backgroundColor: 'var(--background)',
      borderLeft: isFirstRightPinnedColumn
        ? '1px solid var(--border)'
        : undefined,
      borderRight: isLastLeftPinnedColumn
        ? '1px solid var(--border)'
        : undefined,
      boxShadow: edgeShadow,
      left: isPinned === 'left' ? column.getStart('left') : undefined,
      position: 'sticky',
      right: isPinned === 'right' ? column.getAfter('right') : undefined,
      zIndex: isHeader ? 10 : 5
    };
  };

  const table = useReactTable({
    columnResizeMode: 'onChange',
    columns,
    data,
    enableColumnResizing,
    enableMultiRowSelection: false,
    enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getRowId: getRowId ? row => getRowId(row) : undefined,
    getSortedRowModel: getSortedRowModel(),
    initialState: initialColumnPinning
      ? {
          columnPinning: initialColumnPinning
        }
      : undefined,
    manualSorting,
    onColumnSizingChange: handleColumnSizingChange,
    onRowSelectionChange: setRowSelection,
    onSortingChange: handleSortingChange,
    state: {
      columnSizing,
      rowSelection,
      sorting: resolvedSorting
    }
  });

  // Determine if row is selected (either via internal state or external selectedRowId)
  const isRowSelected = (rowId: string) => {
    if (selectedRowId !== undefined) {
      return rowId === selectedRowId;
    }
    return rowSelection[rowId];
  };

  return (
    <Table className={cn('text-sm', className)}>
      <TableHeader>
        {table.getHeaderGroups().map(headerGroup => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map(header => {
              const columnMeta = header.column.columnDef.meta as
                | DataTableColumnMeta
                | undefined;

              return (
                <TableHead
                  className={cn(
                    'relative overflow-hidden',
                    columnMeta ? columnMeta.headerClassName : undefined,
                    header.column.getCanSort() && 'cursor-pointer select-none',
                    header.column.getIsPinned() && 'bg-background'
                  )}
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{
                    ...getCommonPinningStyles(header.column, true),
                    maxWidth: header.getSize(),
                    minWidth: header.getSize(),
                    width: header.getSize()
                  }}
                >
                  {header.isPlaceholder ? null : (
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-1">
                      <div className="min-w-0">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </div>
                      {header.column.getCanSort() &&
                        (header.column.getIsSorted() === 'asc' ? (
                          <ArrowUpIcon className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                        ) : header.column.getIsSorted() === 'desc' ? (
                          <ArrowDownIcon className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                        ) : (
                          <ArrowUpDownIcon className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                        ))}
                    </div>
                  )}
                  {enableColumnResizing &&
                    header.column.getCanResize() &&
                    !header.isPlaceholder && (
                      <div
                        className={cn(
                          'bg-border/40 absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize touch-none opacity-0 transition-opacity select-none hover:opacity-100',
                          header.column.getIsResizing() &&
                            'bg-border opacity-100'
                        )}
                        onClick={event => {
                          event.stopPropagation();
                        }}
                        onDoubleClick={event => {
                          event.stopPropagation();
                          header.column.resetSize();
                        }}
                        onMouseDown={event => {
                          event.stopPropagation();
                          header.getResizeHandler()(event);
                        }}
                        onTouchStart={event => {
                          event.stopPropagation();
                          header.getResizeHandler()(event);
                        }}
                      />
                    )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.length ? (
          table.getRowModel().rows.map(row => (
            <TableRow
              className={cn(
                'h-12',
                (enableRowSelection || onRowClick) && 'cursor-pointer'
              )}
              data-state={isRowSelected(row.id) ? 'selected' : undefined}
              key={row.id}
              onClick={event => {
                if (event.defaultPrevented) {
                  return;
                }

                if (onRowClick) {
                  onRowClick(row.original);
                }

                if (enableRowSelection) {
                  setRowSelection(current =>
                    current[row.id] ? {} : { [row.id]: true }
                  );
                }
              }}
            >
              {row.getVisibleCells().map(cell => {
                const columnMeta = cell.column.columnDef.meta as
                  | DataTableColumnMeta
                  | undefined;

                return (
                  <TableCell
                    className={cn(
                      columnMeta ? columnMeta.cellClassName : undefined,
                      cell.column.getIsPinned() && 'bg-background'
                    )}
                    key={cell.id}
                    style={{
                      ...getCommonPinningStyles(cell.column),
                      maxWidth: cell.column.getSize(),
                      minWidth: cell.column.getSize(),
                      width: cell.column.getSize()
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                );
              })}
            </TableRow>
          ))
        ) : (
          <TableRow className="hover:bg-transparent">
            <TableCell
              className="p-0"
              colSpan={columns.length}
            >
              {emptyAction ? (
                <Empty className="px-0! pt-4! pb-0!">
                  <EmptyContent>{emptyAction}</EmptyContent>
                </Empty>
              ) : (
                emptyMessage
              )}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function readStoredColumnSizing(storageKey: string): ColumnSizingState {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) {
      return {};
    }

    const parsedValue = JSON.parse(rawValue) as Record<string, unknown>;
    return sanitizeColumnSizing(parsedValue);
  } catch {
    return {};
  }
}

function sanitizeColumnSizing(
  value: ColumnSizingState | Record<string, unknown>
): ColumnSizingState {
  if (!value || typeof value !== 'object') {
    return {};
  }

  const sanitized: ColumnSizingState = {};

  for (const [columnId, widthValue] of Object.entries(value)) {
    if (typeof widthValue !== 'number' || !Number.isFinite(widthValue)) {
      continue;
    }

    const clampedWidth = Math.max(
      MIN_COLUMN_SIZE,
      Math.min(MAX_COLUMN_SIZE, widthValue)
    );
    sanitized[columnId] = clampedWidth;
  }

  return sanitized;
}

function writeStoredColumnSizing(
  storageKey: string,
  columnSizing: ColumnSizingState
) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const safeColumnSizing = sanitizeColumnSizing(columnSizing);
    window.localStorage.setItem(storageKey, JSON.stringify(safeColumnSizing));
  } catch {
    // Swallow storage errors to avoid breaking table rendering
  }
}
